import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Fade,
} from '@mui/material';
import {
  Add,
  KeyboardArrowRight,
  OpenInNew,
  InfoOutlined,
} from '@mui/icons-material';
import { useAppSelector } from '../store/hooks';
import { useNavigate, useLocation } from 'react-router-dom';
import UserManagement from './UserManagement';
import CustomerProfiling from './CustomerProfiling';

const TEAMS_AGENTS = [
  {
    name: 'ExtractAI',
    description:
      'Extracts structured and unstructured data from PDFs and images. Tabular data comes back as a ready-to-use CSV.',
    icon: '/assets/teams-agents/extractai.svg',
    teamsLink:
      'https://teams.microsoft.com/l/app/?source=app-details-dialog&titleId=T_96c6a35c-06eb-da61-60a1-349505d9a188',
  },
  {
    name: 'Knowledge Hub Agent',
    description:
      'Official assistant for AGRANA Fruit Knowledge Hub: business insights, recipes, raw materials, development projects, legal and quality guidance.',
    icon: '/assets/teams-agents/knowledge-hub.svg',
    teamsLink:
      'https://teams.microsoft.com/l/app/?source=app-details-dialog&titleId=T_9e04a3bc-0e46-3814-3f69-105253bf3a71',
  },
  {
    name: 'ANIS',
    description:
      'AGRANA News Intelligence. Monitors industry news across Fruit, Starch and Sugar and answers questions in English or German with source links.',
    icon: '/assets/teams-agents/anis.svg',
    teamsLink:
      'https://teams.microsoft.com/l/app/36a0d30a-411f-4a5d-a2fb-91d29fe4cb65?source=app-details-dialog',
  },
  {
    name: 'ACOA Agent',
    description:
      'Finds the most relevant financial accounts for a transaction, based on the ACOA_Manual SharePoint list, with direct SharePoint references.',
    icon: '/assets/teams-agents/acoa.svg',
    teamsLink:
      'https://teams.microsoft.com/l/app/?source=app-details-dialog&titleId=T_6cf9b681-8713-7f2e-33dc-95ca09a12158',
  },
];

const TEAMS_PURPLE = '#6264a7';

const GettingStarted: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const user = useAppSelector(
    (state: { auth: { user: any } }) => state.auth.user
  );

  // Check if we're showing user management (based on hash)
  const showUserManagement = location.hash === '#user-management';
  const showCustomerProfiling = location.hash === '#customer-profiling';

  const [message, setMessage] = useState("");
  const [isMultiline, setIsMultiline] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const textRef = useRef<HTMLTextAreaElement | null>(null);

  // Track caret position
  const caretPosRef = useRef<number | null>(null);

  // Save caret before message changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    caretPosRef.current = e.target.selectionStart;
    setMessage(e.target.value);
  };

  // Window resize listener
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Detect multiline (lines OR char limit exceeded)
  useEffect(() => {
    const charLimit = Math.floor(windowWidth / 8); // tweak divisor for accuracy
    const lineCount = message.split("\n").length;
    setIsMultiline(lineCount > 1 || message.length > charLimit);
  }, [message, windowWidth]);



    // Restore caret after multiline toggle
    useEffect(() => {
      if (textRef.current && caretPosRef.current !== null) {
        textRef.current.focus();
        textRef.current.setSelectionRange(caretPosRef.current, caretPosRef.current);
      }
    }, [isMultiline]);

  // Allow access to all authenticated users
  if (!user) {
    navigate("/login");
    return null;
  }

  const handleSendMessage = () => {
    if (!message.trim()) return;
    console.log("Send:", message);
    setMessage("");
  };

  // Detect if we're on staging based on URL path
  const isStaging = window.location.pathname.startsWith('/staging');
  const docupediaUrl = isStaging ? '/staging/docupedia/' : '/docupedia/';

  // IMPORTANT: app is served under /staging on staging domain; absolute /assets/... breaks.
  // CRA injects PUBLIC_URL at build-time (set to "/staging" in deploy workflow).
  const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const assetUrl = (path: string) => `${publicUrl}${path.startsWith("/") ? path : `/${path}`}`;

  const featureCards = [
    {
      title: "AGRANA News Intelligence System (ANIS)",
      description:
        "Your AI-powered newsletter assistant with intelligent chatbot, email search, web research, and comprehensive analytics.",
      buttonText: "Enter ANIS",
      image: assetUrl("/assets/generatethenewsletter.png"),
      onClick: () => navigate("/anis/dashboard"),
    },
    {
      title: "PDMI Knowledge Hub",
      description:
        "Search presentation slides and documents with AI-powered semantic search. Get instant answers with source citations from your knowledge base.",
      buttonText: "Enter PDMI Knowledge Hub",
      image: assetUrl("/assets/docupedia-card-2026.png"),
      onClick: () => {
        const token = localStorage.getItem('aihub_token');
        // Chainlit calls /auth/header without query params, so relying on `?token=` breaks.
        // Persist token in a cookie so every subsequent Chainlit request carries it.
        if (token) {
          document.cookie = `aihub_token=${encodeURIComponent(token)}; Path=/; Max-Age=3600; Secure; SameSite=Lax`;
        }
        window.location.href = docupediaUrl;
      },
    },
    {
      title: "Customer Profiling",
      description:
        "Build comprehensive customer profiles using AI analysis of behavior patterns, preferences, and engagement data.",
      buttonText: "Create profiles",
      image: assetUrl("/assets/customerprofiling.png"),
      onClick: () => navigate("/cp"),
    },
    {
      title: "Predictive Insights",
      description:
        "Get AI-powered predictions for content performance, optimal send times, and subscriber engagement trends.",
      buttonText: "Coming soon",
      image: assetUrl("/assets/predictiveinsights.png"),
      onClick: () => {},
      disabled: true,
    },
  ];

  // If showing user management, render that component instead
  if (showUserManagement) {
    return <UserManagement />;
  }

  if (showCustomerProfiling) {
    return <CustomerProfiling />;
  }

  return (
      <Container sx={{
        py: isMobile ? 2 : 4,
        px: isMobile ? 2 : 3,
        maxWidth: '100%',
        width: '100%',
      }}>
      {/* Welcome Section */}
      <Fade in timeout={600}>
        <Box sx={{ mb: isMobile ? 3 : 4 }}>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            component="h1"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 1,
              textAlign: 'left',
            }}
          >
            Welcome to your AI Hub, {user?.username || 'User'}
          </Typography>
        </Box>
      </Fade>

      {/* AI Chat Box */}
      <Fade in timeout={800}>
        <Card
          sx={{
            mb: isMobile ? 3 : 4,
            p: isMobile ? 3 : 4,
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Typography
              variant={isMobile ? "h4" : "h3"}
              component="h3"
              sx={{
                textAlign: 'center',
                mb: 3,
                fontWeight: 500,
                color: 'text.primary',
                fontSize: isMobile ? '1.5rem' : '2rem'
              }}
            >
              Hello, how can I help?
            </Typography>
          
            
            <Box
              sx={{
                backgroundColor: "#f3f4f6", // ✅ light gray background
                borderRadius: "16px",
                border: "1px solid #e5e7eb",
                p: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                transition: "all 0.25s ease-in-out", // ✅ smooth animation
              }}
            >
              {isMultiline ? (
                <>
                  {/* Row 1 → Text full width */}
                  <TextField
                    inputRef={textRef}
                    fullWidth
                    multiline
                    maxRows={6}
                    placeholder="Message to A.V.A"
                    value={message}
                    onChange={handleChange}
                    variant="outlined"
                    inputProps={{
                      'aria-label': 'Type your message'
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        border: "none",
                        backgroundColor: "transparent",
                        fontSize: "0.9rem",
                        padding: "8px 12px",
                        transition: "all 0.25s ease-in-out",
                      },
                      "& fieldset": { border: "none" },
                    }}
                  />

                  {/* Row 2 → + left, send right */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      transition: "all 0.25s ease-in-out",
                    }}
                  >
                    <IconButton
                      sx={{
                        backgroundColor: "#f3f4f6",
                        color: "text.secondary",
                        "&:hover": { backgroundColor: "#e5e7eb" },
                        width: 32,
                        height: 32,
                      }}
                    >
                      <Add fontSize="small" />
                    </IconButton>

                    <IconButton
                      onClick={handleSendMessage}
                      disabled={!message.trim()}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: "white",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                        width: 32,
                        height: 32,
                        transition: "background-color 0.2s ease-in-out",
                      }}
                    >
                      <KeyboardArrowRight fontSize="small" />
                    </IconButton>
                  </Box>
                </>
              ) : (
                /* ✅ Single-line case → all inline */
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 1,
                    transition: "all 0.25s ease-in-out",
                  }}
                >
                  <IconButton
                    sx={{
                      backgroundColor: "#f3f4f6",
                      color: "text.secondary",
                      "&:hover": { backgroundColor: "#e5e7eb" },
                      width: 32,
                      height: 32,
                    }}
                  >
                    <Add fontSize="small" />
                  </IconButton>

                  <TextField
                    key="message-input"
                    inputRef={textRef}
                    fullWidth
                    multiline
                    maxRows={6}
                    placeholder="Message to A.V.A"
                    value={message}
                    onChange={handleChange}
                    variant="outlined"
                    inputProps={{
                      'aria-label': 'Type your message'
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        border: "none",
                        backgroundColor: "transparent",
                        fontSize: "0.9rem",
                        padding: "4px 8px",
                        transition: "all 0.25s ease-in-out",
                      },
                      "& fieldset": { border: "none" },
                    }}
                  />

                  <IconButton
                    onClick={handleSendMessage}
                    disabled={!message.trim()}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      width: 32,
                      height: 32,
                      transition: "background-color 0.2s ease-in-out",
                    }}
                  >
                    <KeyboardArrowRight fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>


          </CardContent>
        </Card>
      </Fade>

      {/* Feature Cards */}
      <Fade in timeout={1000}>
        <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
          {featureCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={6} lg={4} key={index}>
              <Card
                sx={{
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: card.disabled ? 'default' : 'pointer',
                  opacity: card.disabled ? 0.6 : 1,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  ...(!card.disabled && {
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      borderColor: '#a31830',
                      transform: 'translateY(-2px)',
                    },
                  }),
                }}
                onClick={card.disabled ? undefined : card.onClick}
              >
                <Box
                  sx={{
                    height: isMobile ? 150 : 180,
                    overflow: 'hidden',
                    p: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      backgroundImage: `url(${card.image})`,
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      borderRadius: '8px',
                    }}
                  />
                </Box>

                <CardContent
                  sx={{
                    p: isMobile ? 2.5 : 3,
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography
                      variant={isMobile ? "subtitle1" : "h6"}
                      component="h3"
                      sx={{
                        fontWeight: 500,
                        color: '#1f2937',
                        mb: 2,
                        lineHeight: 1.4,
                        fontSize: isMobile ? '1rem' : '1.125rem',
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#6b7280',
                        mb: 2.5,
                        fontSize: isMobile ? '0.85rem' : '0.95rem',
                        lineHeight: 1.6,
                      }}
                    >
                      {card.description}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="medium"
                    disabled={card.disabled}
                    sx={{
                      backgroundColor: '#a31830',
                      color: 'white',
                      textTransform: 'none',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      py: 1,
                      px: 2.5,
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                      '&:hover': {
                        backgroundColor: '#8b1426',
                        boxShadow: '0 4px 12px rgba(163, 24, 48, 0.25)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!card.disabled) card.onClick();
                    }}
                  >
                    {card.buttonText}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Fade>

      {/* Connected Agents */}
      <Fade in timeout={1200}>
        <Box sx={{ mb: isMobile ? 3 : 4 }}>
          <Divider
            sx={{
              mb: isMobile ? 2.5 : 3,
              '&::before, &::after': { borderColor: '#e5e7eb' },
              '& .MuiDivider-wrapper': {
                color: '#9ca3af',
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.08em',
              },
            }}
          >
            CONNECTED AGENTS
          </Divider>

          <Box
            sx={{
              backgroundColor: '#fbf6f7',
              border: '1px solid #f0dfe3',
              borderLeft: `4px solid #a31830`,
              borderRadius: '12px',
              p: isMobile ? 2 : 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'flex-start' : 'center',
                justifyContent: 'space-between',
                gap: isMobile ? 1.5 : 2,
                mb: isMobile ? 2 : 2.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    borderRadius: '8px',
                    backgroundColor: TEAMS_PURPLE,
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.95rem',
                    fontWeight: 700,
                  }}
                >
                  T
                </Box>
                <Box>
                  <Typography
                    sx={{
                      color: TEAMS_PURPLE,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      lineHeight: 1.4,
                    }}
                  >
                    MICROSOFT TEAMS
                  </Typography>
                  <Typography
                    component="h2"
                    sx={{
                      fontWeight: 600,
                      color: '#1f2937',
                      fontSize: isMobile ? '1.05rem' : '1.15rem',
                      lineHeight: 1.3,
                    }}
                  >
                    Copilot Agents
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#6b7280', fontSize: '0.85rem', mt: 0.25 }}
                  >
                    Purpose-built assistants that open in a Teams chat — separate from the Hub apps above.
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  flexShrink: 0,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: '999px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  color: TEAMS_PURPLE,
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: TEAMS_PURPLE,
                  }}
                />
                Opens in Microsoft Teams
                <OpenInNew sx={{ fontSize: '0.85rem' }} />
              </Box>
            </Box>

            <Grid container spacing={isMobile ? 1.5 : 2}>
              {TEAMS_AGENTS.map((agent) => {
                const available = Boolean(agent.teamsLink);
                return (
                  <Grid item xs={12} sm={6} md={4} key={agent.name}>
                    <Card
                      sx={{
                        backgroundColor: '#ffffff',
                        borderRadius: '10px',
                        border: '1px solid #e5e7eb',
                        borderLeft: '3px solid #a31830',
                        boxShadow: 'none',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <CardContent
                        sx={{
                          p: 2,
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          '&:last-child': { pb: 2 },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                          <Box
                            component="img"
                            src={assetUrl(agent.icon)}
                            alt=""
                            sx={{
                              width: 32,
                              height: 32,
                              flexShrink: 0,
                              borderRadius: '8px',
                              display: 'block',
                            }}
                          />
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography
                              component="h3"
                              sx={{
                                fontWeight: 600,
                                color: '#1f2937',
                                fontSize: '0.95rem',
                                lineHeight: 1.3,
                              }}
                            >
                              {agent.name}
                            </Typography>
                            <Box
                              sx={{
                                px: 0.75,
                                borderRadius: '4px',
                                backgroundColor: '#eef0f9',
                                color: TEAMS_PURPLE,
                                fontSize: '0.62rem',
                                fontWeight: 700,
                                letterSpacing: '0.05em',
                                lineHeight: 1.8,
                              }}
                            >
                              AGENT
                            </Box>
                          </Box>
                        </Box>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#6b7280',
                            fontSize: '0.85rem',
                            lineHeight: 1.55,
                            mb: 2,
                            flexGrow: 1,
                          }}
                        >
                          {agent.description}
                        </Typography>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                              sx={{
                                width: 7,
                                height: 7,
                                borderRadius: '50%',
                                backgroundColor: available ? '#22c55e' : '#d1d5db',
                              }}
                            />
                            <Typography
                              sx={{
                                color: '#6b7280',
                                fontSize: '0.78rem',
                              }}
                            >
                              {available ? 'Available' : 'Not configured'}
                            </Typography>
                          </Box>

                          <Button
                            variant="outlined"
                            size="small"
                            disabled={!available}
                            endIcon={<OpenInNew sx={{ fontSize: '0.9rem' }} />}
                            onClick={() =>
                              window.open(
                                agent.teamsLink,
                                '_blank',
                                'noopener,noreferrer'
                              )
                            }
                            sx={{
                              textTransform: 'none',
                              borderRadius: '8px',
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                              color: TEAMS_PURPLE,
                              borderColor: '#c7cae4',
                              '&:hover': {
                                borderColor: TEAMS_PURPLE,
                                backgroundColor: '#f4f5fb',
                              },
                            }}
                          >
                            Open in Teams
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.75,
                mt: isMobile ? 2 : 2.5,
                color: '#6b7280',
              }}
            >
              <InfoOutlined sx={{ fontSize: '1rem', mt: '1px', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
                These agents run in Microsoft Teams. Selecting one opens it in the Teams desktop or
                web client — it does not run inside the Hub.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Container>
  );
};

export default GettingStarted;
